import {CommonService} from '../../services/common.service';
import {LanguageCode} from '../../../core/constants/languages.constants';
import {ToastMessageTypeEnum} from '../../../typings/enums/toast-message-type.enum';
import {ToastDurationTypeEnum} from '../../../typings/enums/toast-duration-type.enum';
import {APPLICATION_ROUTES_CONSTANTS} from '../../../core/constants/application-routes.constants';
import {inject, PLATFORM_ID, Type} from '@angular/core';
import {MESSAGES_CONSTANTS} from '../../../core/constants/messages.constants';
import {ConfirmationDialogModel} from '../../../typings/models/confirmation-dialog.model';
import {environment} from '../../../../environments/environment';
import {formatDateTimeDisplay} from '../../../core/utilities/date-time.utilities';
import {ImportSettingOptionModel} from '../../../typings/models/import-setting-option.model';
import {ASSETS_CONSTANTS} from '../../../core/constants/assets.constants';
import {LoadingService} from '../../services/loading.service';
import {isPlatformBrowser, Location} from '@angular/common';
import {Router} from '@angular/router';

export abstract class TBaseCommonComponent {

  protected commonService = inject(CommonService);
  public location = inject(Location);
  public loadingService = inject(LoadingService);
  protected platformId = inject(PLATFORM_ID);
  protected router = inject(Router);

  protected canGoBackToPreviousRoute = false;

  public defaultPreviousRoute: any = APPLICATION_ROUTES_CONSTANTS.ROUTES.SOURCES;

  protected environment = environment;

  get importSettingOptions(): ImportSettingOptionModel[] {
    return this.environment.importSetting.options as ImportSettingOptionModel[];
  }

  protected readonly formatDateTimeDisplay = formatDateTimeDisplay;
  protected readonly APPLICATION_ROUTES_CONSTANTS = APPLICATION_ROUTES_CONSTANTS;
  protected readonly ASSETS_CONSTANTS = ASSETS_CONSTANTS;

  userInformation = () => this.commonService.userInformation;
  errorMessage = '';

  constructor() {
    this.canGoBackToPreviousRoute = !!(this.router.lastSuccessfulNavigation?.previousNavigation);
  }

  onInitialization(): void {
  }

  goBackToPreviousRoute(): void {
    if (this.canGoBackToPreviousRoute) {
      this.location.back();
    } else {
      this.navigateTo(this.defaultPreviousRoute);
    }
  }

  sendEmailToSupport = async (subject?: string, body?: string) => {
    const supportEmail = this.environment.contactEmail;
    const appName = this.environment.applicationName;
    const user = this.commonService?.userInformation;

    const suggestedBody = (user ? `User: ${user?.firstName} - (${user.email})\n` : ``) +
      `App: ${appName} ${this.environment?.version ?? ''}\n` +
      `Date: ${this.formatDateTimeDisplay(new Date())}\n\nDescribe the issue:\n`;

    const defaultSubject = subject ?? (`${appName} Support Request` + (user ? ` - ${user.email}` : ``));
    const defaultBody = body ?? suggestedBody;

    const mailto = `mailto:${supportEmail}?subject=${encodeURIComponent(defaultSubject)}&body=${encodeURIComponent(defaultBody)}`;

    // Try Web Share API (mobile browsers) first
    try {
      if (navigator && (navigator as any).share) {
        await (navigator as any).share({title: defaultSubject, text: defaultBody});
        return;
      }
    } catch {
    }

    // Fallback: open mailto link (works in desktop browsers and mobile webviews)
    try {
      const a = document.createElement('a');
      a.href = mailto;
      a.rel = 'noopener noreferrer';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      try {
        window.location.href = mailto;
      } catch {
        this.showToast('Unable to open email client', ToastMessageTypeEnum.Error);
      }
    }
  }

  openInstagram(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const instagramUrl = environment.socialMediaLinks.instagram;

    if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined' && typeof window.open === 'function') {
      // Open in a new browser tab/window
      window.open(instagramUrl, '_blank', 'noopener,noreferrer');
    } else {
      // For mobile/webview or non-browser environments, navigate using location
      location.assign(instagramUrl);
    }
  }

  openSocialLink(socialLink: string, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined' && typeof window.open === 'function') {
      // Open in a new browser tab/window
      window.open(socialLink, '_blank', 'noopener,noreferrer');
    } else {
      // For mobile/webview or non-browser environments, navigate using location
      location.assign(socialLink);
    }
  }

  changeLanguage(languageCode: LanguageCode): void {
    this.commonService.changeLanguage(languageCode);
  }

  currentLanguageCode() {
    return this.commonService.currentLanguageCode;
  }

  getSupportedLanguages() {
    return this.commonService.getSupportedLanguages();
  }

  navigateTo(route: string, state?: any | null) {
    return this.commonService.navigateTo(route, state);
  }

  showToast(message: string, type?: ToastMessageTypeEnum, duration?: ToastDurationTypeEnum) {
    return this.commonService.showToast(message, type, duration);
  }

  openDialog<T>(
    component: Type<T>,
    inputs?: Partial<T>
  ) {
    return this.commonService.openDialog<T>(component, inputs);
  }

  closeDialog = () => this.commonService.closeDialog();

  protected unexpectedErrorHandler(error: any) {
    console.error('Unexpected error: ', error);
    this.showToast(MESSAGES_CONSTANTS.UNEXPECTED_ERROR, ToastMessageTypeEnum.Error);
    throw error;
  }

  protected showConfirmationDialog
    = (data: ConfirmationDialogModel, widthSize?: string) => this.commonService.showConfirmationDialog(data, widthSize);
}
